import { Component, HostBinding, Input, ChangeDetectionStrategy, ViewEncapsulation, OnInit, OnDestroy, ElementRef, ViewChild, OnChanges,
    ChangeDetectorRef, Output, EventEmitter, forwardRef, SimpleChanges, Directive, ContentChild, QueryList, Renderer2, AfterViewInit,
    TemplateRef, ContentChildren } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { startWith, takeUntil } from 'rxjs/operators';
import { Subject, BehaviorSubject, of, Observable } from 'rxjs';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipList } from '@angular/material/chips';
import { CdkDropList } from '@angular/cdk/drag-drop';

import { BaseDirective } from '../../base.directive';
import { EuiAutoCompleteItem } from './models/eui-autocomplete-item.model';
import { EuiChip } from '../eui-chip/models/eui-chip.model';
import { EuiChipDragDrop } from '../eui-chip-list';
import { EuiTemplateDirective } from '../../directives';

@Component({
    templateUrl: './eui-autocomplete.component.html',
    selector: 'eui-autocomplete, input[euiAutocomplete]',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => EuiAutocompleteComponent),
        multi: true,
    }],
})
export class EuiAutocompleteComponent extends BaseDirective implements OnInit, OnDestroy, ControlValueAccessor, OnChanges, AfterViewInit {
    @Input()
    get hasChips() { return this._hasChips; }
    set hasChips(value) { this._hasChips = coerceBooleanProperty(value); }
    _hasChips = false;

    @Input()
    get async() { return this._async; }
    set async(value) { this._async = coerceBooleanProperty(value); }
    _async = false;

    @Input() autocompleteData: EuiAutoCompleteItem[] = [];
    @Input() autocompleteDataSelected: EuiAutoCompleteItem[] = [];

    @Input()
    get addOnBlur() { return this._addOnBlur; }
    set addOnBlur(value) { this._addOnBlur = coerceBooleanProperty(value); }
    _addOnBlur = false;

    @Input()
    get isFreeValueAllowed() { return this._isFreeValueAllowed; }
    set isFreeValueAllowed(value) { this._isFreeValueAllowed = coerceBooleanProperty(value); }
    _isFreeValueAllowed = true;

    @Input()
    get isDuplicateValueAllowed() { return this._isDuplicateValueAllowed; }
    set isDuplicateValueAllowed(value) { this._isDuplicateValueAllowed = coerceBooleanProperty(value); }
    _isDuplicateValueAllowed = false;

    @Input() chipsSortOrder: ('ASC' | 'DESC') = 'ASC';
    @Input() itemsSortOrder: ('ASC' | 'DESC') = 'ASC';
    @Input() placeholder = '';
    @Input() matching: ('startWith' | 'contains') = 'contains';
    @Input() chipsPosition: ('top' | 'bottom' | 'inside') = 'top';
    @Input() panelWidth: number;
    @Input() classList: string;
    @Input() groupBy: string;

    @Input()
    get isLoading() { return this._isLoading; }
    set isLoading(value) { this._isLoading = coerceBooleanProperty(value); }
    _isLoading = false;

    // Chips
    @Input()
    get isChipsRemovable() { return this._isChipsRemovable; }
    set isChipsRemovable(value) { this._isChipsRemovable = coerceBooleanProperty(value); }
    _isChipsRemovable = true;

    @Input()
    get maxVisibleChipsCount() { return this._maxVisibleChipsCount; }
    set maxVisibleChipsCount(value) { this._maxVisibleChipsCount = coerceNumberProperty(value); }
    _maxVisibleChipsCount = null;

    @Input()
    get chipsLabelTruncateCount() { return this._chipsLabelTruncateCount; }
    set chipsLabelTruncateCount(value) { this._chipsLabelTruncateCount = coerceNumberProperty(value); }
    _chipsLabelTruncateCount = null;

    @Input()
    get isMaxVisibleChipsOpened() { return this._isMaxVisibleChipsOpened; }
    set isMaxVisibleChipsOpened(value) { this._isMaxVisibleChipsOpened = coerceBooleanProperty(value); }
    _isMaxVisibleChipsOpened = false;

    @Input()
    get readonly() { return this._readonly; }
    set readonly(value) { this._readonly = coerceBooleanProperty(value); }
    _readonly = false;

    @Input() toggleLinkMoreLabel: string = null;
    @Input() toggleLinkLessLabel: string = null;

    @Input()
    get isChipsSorted() { return this._isChipsSorted; }
    set isChipsSorted(value) { this._isChipsSorted = coerceBooleanProperty(value); }
    _isChipsSorted = false;

    @Input()
    get isItemsSorted() { return this._isItemsSorted; }
    set isItemsSorted(value) { this._isItemsSorted = coerceBooleanProperty(value); }
    _isItemsSorted = false;

    @Input()
    get isChipsDragAndDrop() { return this._isChipsDragAndDrop; }
    set isChipsDragAndDrop(value) { this._isChipsDragAndDrop = coerceBooleanProperty(value); }
    _isChipsDragAndDrop = false;

    @Input() dragAndDropSourceName: string;
    @Input() dragAndDropConnectedTo: string[] = [];

    @Output() closed = new EventEmitter();
    @Output() opened = new EventEmitter();
    @Output() selectionChanged = new EventEmitter<EuiAutoCompleteItem[]>();
    @Output() itemAdded = new EventEmitter<EuiAutoCompleteItem>();
    @Output() itemRemoved = new EventEmitter<EuiAutoCompleteItem>();
    @Output() inputChanged = new EventEmitter<string>();
    @Output() chipDragStarted = new EventEmitter<EuiChipDragDrop>();
    @Output() chipDragReleased = new EventEmitter<EuiChipDragDrop>();
    @Output() chipDropped = new EventEmitter<EuiChipDragDrop>();
    @Output() clear = new EventEmitter();

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-autocomplete'),
        ].join(' ').trim();
    }

    @ContentChild(forwardRef(() => EuiAutocompletePrependContentDirective))
        euiAutocompletePrependContent: QueryList<EuiAutocompletePrependContentDirective>;
    @ContentChild(forwardRef(() => EuiAutocompleteAppendContentDirective))
        euiAutocompleteAppendContent: QueryList<EuiAutocompleteAppendContentDirective>;
    @ContentChildren(EuiTemplateDirective) templates: QueryList<EuiTemplateDirective>;

    @ViewChild('autocompleteInput') autocompleteInput: ElementRef<HTMLInputElement>;
    @ViewChild('autocompleteInputTrigger', { read: MatAutocompleteTrigger }) autocompleteInputTrigger: MatAutocompleteTrigger;
    @ViewChild('auto') matAutocomplete: MatAutocomplete;

    public autocompleteOptionTemplate: TemplateRef<any>;
    public autocompleteOptGroupTemplate: TemplateRef<any>;

    public separatorKeysCodes: number[] = [ENTER, COMMA];
    public autocompleteControl = new FormControl();
    public autocompleteOptions: BehaviorSubject<EuiAutoCompleteItem[]> = new BehaviorSubject<EuiAutoCompleteItem[]>(this.autocompleteData);
    public chips: EuiChip[] = [];
    public chipsListComponent: MatChipList;

    public distinctOptionGroups: string[];
    public groupedItems: { [id: string]: Observable<EuiAutoCompleteItem[]> };

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private nbClick = 0;
    private ignoreOpenCloseEvents = false;

    constructor(private cd: ChangeDetectorRef, protected _renderer: Renderer2) {
        super();
    }

    ngOnChanges(c: SimpleChanges) {
        if (c && c.autocompleteData) {
            this.autocompleteOptions.next(this._filter(''));

            if (this.groupBy) {
                this.groupingHandler();
            }
        }

        if (c && c.readonly && !c.readonly.firstChange) {
            this.readonlyHandler();
        }
    }

    ngOnInit() {
        this.autocompleteControl.valueChanges.pipe(takeUntil(this.destroy$), startWith(null)).subscribe((value) => {
            if (this.hasChips) {
                this.autocompleteDataSelected = this.autocompleteDataSelected.map((selected) => {
                    return new EuiAutoCompleteItem(Object.assign({}, selected, { euiInternalId: this.generateId() }));
                });

                this.chips = this.mapAsChip();
                this.onChange(this.autocompleteDataSelected.length > 0 ? this.autocompleteDataSelected : null);
            } else {
                const foundItem = this.autocompleteData.find((data) => {
                    return data && data.label && value && typeof value === 'string' ?
                        data.label.toLowerCase().trim() === value.toLowerCase().trim() :
                        false;
                });

                if (foundItem) {
                    this.onChange(foundItem);
                } else {
                    value ? this.onChange(new EuiAutoCompleteItem({ label: value })) : this.onChange(null);
                }
            }

            if (this.hasChips && value !== null) {
                this.inputChanged.emit(value);
            }

            if (!this.async) {
                const filterValue = typeof value === 'string' || !value ? value : value.label;
                this.autocompleteOptions.next(this.isItemsSorted ?
                    this.orderItems(this._filter(filterValue), this.itemsSortOrder) :
                    this._filter(filterValue));
            }

            // this.updatePosition();
        });

        if (this.groupBy) {
            this.groupingHandler();
        }
    }

    ngAfterViewInit() {
        this.readonlyHandler();

        this.templates.forEach((item) => {
            if (item.getType() === 'dropdownOption') {
                this.autocompleteOptionTemplate = item.template;
            }
            if (item.getType() === 'dropdownOptGroup') {
                this.autocompleteOptGroupTemplate = item.template;
            }
        });

        if (this.hasChips) {
            this.chips = this.mapAsChip();
            this.cd.detectChanges();
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public add(event: MatChipInputEvent) {
        if (this.isFreeValueAllowed && !this.matAutocomplete.isOpen) {
            if ((event.value || '').trim()) {
                const foundItem = !this.isDuplicateValueAllowed ? this.autocompleteDataSelected.find((selectedItem) => {
                    return selectedItem.label.toString().trim() === event.value.trim();
                }) : false;

                if (!foundItem) {
                    this.autocompleteDataSelected.push(new EuiAutoCompleteItem({ label: event.value.trim() }));
                    this.chips = this.mapAsChip();
                    this.autocompleteInput.nativeElement.value = '';
                    this.autocompleteControl.setValue(null);

                    this.selectionChanged.emit(this.autocompleteDataSelected);
                    this.itemAdded.emit(new EuiAutoCompleteItem({ label: event.value.trim() }));
                }
            }
        }
    }

    public onOptionSelected(event: MatAutocompleteSelectedEvent) {
        const value = event.option.value;

        if (this.hasChips) {
            this.autocompleteDataSelected.push(value);
            this.autocompleteInput.nativeElement.value = '';
            this.autocompleteControl.setValue(null);
        } else {
            this.autocompleteDataSelected = value;
            this.autocompleteInput.nativeElement.value = value.label;
            this.autocompleteControl.setValue(value.label);
            this.onChange(value);
        }

        if (this.hasChips) {
            this.autocompleteOptions.next(this._filter(''));
        }

        this.selectionChanged.emit(this.autocompleteDataSelected);
        this.itemAdded.emit(value);
    }

    public onChipRemove(e: { chips: EuiChip[], removed: EuiChip }) {
        const autocompleteDataRemoved = this.autocompleteDataSelected.find((data) => {
            return data.euiInternalId === e.removed.euiInternalId;
        });

        this.autocompleteDataSelected = this.autocompleteDataSelected.filter((data) => {
            return data.euiInternalId !== e.removed.euiInternalId;
        });

        if (!this.isDuplicateValueAllowed) {
            this.autocompleteOptions.next(this.isItemsSorted ? this.orderItems(this._filter(''), this.itemsSortOrder) : this._filter(''));
        }

        this.onChange(this.autocompleteDataSelected.length > 0 ? this.autocompleteDataSelected : null);
        this.selectionChanged.emit(this.autocompleteDataSelected);
        this.itemRemoved.emit(autocompleteDataRemoved);
    }

    public chipsInit(e: { chipList: MatChipList, chipsListDragAndDropItems: CdkDropList }) {
        this.chipsListComponent = e.chipList;
        this.cd.detectChanges();
    }

    public onClick(e: Event) {
        if (!this.readonly) {
            if (this.nbClick > 0) {
                this.matAutocomplete.isOpen ? this.autocompleteInputTrigger.closePanel() : this.autocompleteInputTrigger.openPanel();
            }
            this.nbClick += 1;
        }
    }

    public resetNbClick(e: FocusEvent) {
        if (!e.relatedTarget || e.relatedTarget['className'].indexOf('mat-option') === -1) {
            this.nbClick = 0;
        }
    }

    public trackByFn(index: number, item: EuiAutoCompleteItem) {
        return index;
    }

    public onClose() {
        if (!this.ignoreOpenCloseEvents) {
            this.closed.emit();
        }
    }

    public onOpen() {
        if (!this.ignoreOpenCloseEvents) {
            this.opened.emit();
        }
    }

    // TODO: Next lines are related to: https://citnet.tech.ec.europa.eu/CITnet/jira/browse/EUI-4612
    // When eui-autocomplete is used in a modal, combination of two cdk-overlay cause issue in autocomplete to reposition suggestions
    // panel. Needs to be refactored with a better solution to handle the case in the component itself.
    public updatePosition(): void {
        if (this.autocompleteInputTrigger) {
            this.ignoreOpenCloseEvents = true;

            this.autocompleteInputTrigger.closePanel();
            this.autocompleteInputTrigger.openPanel();

            this.ignoreOpenCloseEvents = false;
        }
    }

    public writeValue(value: any) {
        if (this.hasChips) {
            this.autocompleteControl.setValue(null);
            this.autocompleteDataSelected = value ? value : [];
            this.autocompleteDataSelected = this.autocompleteDataSelected.map((selected) => {
                return new EuiAutoCompleteItem(Object.assign({}, selected, { euiInternalId: this.generateId() }));
            });
            this.chips = [...this.mapAsChip()];

            this.autocompleteOptions.next(this._filter(''));
            this.onChange(this.autocompleteDataSelected.length > 0 ? this.autocompleteDataSelected : null);
        } else {
            this.autocompleteControl.patchValue(value ? value.label : null);
        }
    }

    public setDisabledState(isDisabled: boolean) {
        isDisabled ? this.autocompleteControl.disable() : this.autocompleteControl.enable();
    }

    public registerOnChange(fn: any) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    public clearInput() {
        this.autocompleteInput.nativeElement.value = '';
        this.autocompleteControl.patchValue('');
        if (!this.hasChips) {
            this.onChange(null);
        }
        this.autocompleteInput.nativeElement.focus();
        this.clear.emit();
    }

    public onChipDropped(e: any) {
        this.chipDropped.emit(e);
    }

    public onChipDragStarted(e: any) {
        this.chipDragStarted.emit(e);
    }

    public onChipDragReleased(e: any) {
        this.chipDropped.emit(e);
    }

    private onChange: any = () => {};

    private onTouch: any = () => {};

    private readonlyHandler() {
        if (this.readonly) {
            this._renderer.setAttribute(this.autocompleteInput.nativeElement, 'readonly', 'true');
        } else {
            this._renderer.removeAttribute(this.autocompleteInput.nativeElement, 'readonly');
        }
    }

    private _filter(str: string) {
        const filteredItems = [];
        this.autocompleteData.forEach((item: EuiAutoCompleteItem) => {
            const foundItem = !this.isDuplicateValueAllowed && Array.isArray(this.autocompleteDataSelected) ?
                this.autocompleteDataSelected.find((selectedItem: EuiAutoCompleteItem) => {
                    if (item.id) {
                        return selectedItem.id === item.id;
                    } else {
                        return selectedItem.label.toLowerCase() === item.label.toLowerCase();
                    }
                }) :
                false;

            if (!foundItem) {
                if (str) {
                    if (this.matching === 'contains' && item.label.toLowerCase().indexOf(str.toString().toLowerCase()) !== -1) {
                        filteredItems.push(item);
                    }
                    if (this.matching === 'startWith' && item.label.toLowerCase().substr(0, str.toString().length) === str.toLowerCase()) {
                        filteredItems.push(item);
                    }
                } else {
                    filteredItems.push(item);
                }
            }
        });

        return this.isItemsSorted ? this.orderItems(filteredItems, this.itemsSortOrder) : filteredItems;
    }

    private mapAsChip() {
        const chips = this.autocompleteDataSelected.map((selected) => {
            return new EuiChip({
                id: selected.id,
                euiInternalId: selected.euiInternalId,
                label: selected.label,
                typeClass: selected.typeClass,
                isOutline: selected.isOutline,
                isRounded: selected.isRounded,
                sizeClass: selected.sizeClass,
                isRemovable: selected.isRemovable,
                tooltipMessage: selected.tooltip?.tooltipMessage,
                tooltip: selected.tooltip,
                iconClass: selected.iconClass,
            });
        });

        return this.isChipsSorted ? this.orderItems(chips, this.chipsSortOrder) : chips;
    }

    private generateId(): string {
        return '_' + Math.random().toString(36).substr(2, 9);
    }

    private orderItems(tab: any, sortOrder: string) {
        tab.sort((a: any, b: any) => {

            const aObj = a.label.toLowerCase();
            const bObj = b.label.toLowerCase();

            if (sortOrder === 'ASC') {
                if (aObj < bObj) {
                    return -1;
                }
                if (aObj > bObj) {
                    return 1;
                }
                return 0;
            } else {
                if (aObj > bObj) {
                    return -1;
                }
                if (aObj < bObj) {
                    return 1;
                }
                return 0;
            }
        });

        return tab;
    }

    private groupingHandler(): void {
        this.distinctOptionGroups = this.autocompleteData.map((item) => {
            return this.groupBy.split('.').reduce((prev, curr) => {
                return prev ? prev[curr] : null;
            }, item || self);
        });

        this.distinctOptionGroups = this.distinctOptionGroups
            .filter((n, i) => this.distinctOptionGroups.indexOf(n) === i)
            .sort((a: string, b: string) => a > b ? 1 : -1);

        this.autocompleteOptions.subscribe((autocompleteOptions) => {
            let groupedItems: EuiAutoCompleteItem[];
            this.distinctOptionGroups.forEach((group) => {
                groupedItems = autocompleteOptions.filter((item) => {
                    const f = this.groupBy.split('.').reduce((prev, curr) => {
                        return prev ? prev[curr] : null;
                    }, item || self);

                    return f === group;
                });

                if (this.isItemsSorted) {
                    groupedItems = this.orderItems(groupedItems, this.itemsSortOrder);
                }

                this.groupedItems = {
                    ...this.groupedItems,
                    [group]: of(groupedItems),
                } as { [id: string]: Observable<EuiAutoCompleteItem[]> };
            });
        });
    }
}

/* tslint:disable */
@Directive({ selector: 'euiAutocompletePrependContent' })
export class EuiAutocompletePrependContentDirective { }

@Directive({ selector: 'euiAutocompleteAppendContent' })
export class EuiAutocompleteAppendContentDirective { }
/* tslint:enable */
